import { lazy, Suspense, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RedirectIfAuthed } from '@/components/RedirectIfAuthed';
import { ExternalRedirect } from '@/components/ExternalRedirect';
import { SiteHeader } from '@/components/SiteHeader';
import { RouteSeo } from '@/components/RouteSeo';
import { MobileTabBar, isTabBarHidden } from '@/components/mobile/MobileTabBar';
import { ToastViewport } from '@/components/Toast';
import { DRAFTEDGE, draftedgeSso } from '@/lib/draftedge';
import { isNativeApp } from '@/lib/platform';
import { AppHeader } from '@/components/mobile/AppHeader';
import { HomeSidebar } from '@/components/mobile/HomeSidebar';
import { useAuthStore } from '@/lib/auth';
import { BootScreen } from '@/components/BootScreen';
import { useAndroidBackButton } from '@/hooks/useAndroidBackButton';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Lazy-loaded route bundles — keeps initial payload small for mobile WebView.
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const SsoCallback = lazy(() => import('./pages/SsoCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Account = lazy(() => import('./pages/Account'));
// NOTE: Register / ForgotPassword / Pricing pages are intentionally NOT
// imported — those routes now redirect to draftedge.com (signup + billing
// front door). See the route definitions below + src/lib/draftedge.ts.

// NFL fantasy football draft assistant (the actual product).
const Drafts = lazy(() => import('./pages/Drafts'));
const DraftDetail = lazy(() => import('./pages/DraftDetail'));
const DraftNew = lazy(() => import('./pages/DraftNew'));
const Rankings = lazy(() => import('./pages/Rankings'));
const Cheatsheet = lazy(() => import('./pages/Cheatsheet'));
const Compare = lazy(() => import('./pages/Compare'));
const Rookies = lazy(() => import('./pages/Rookies'));
const PlayerDetail = lazy(() => import('./pages/PlayerDetail'));

// News list + detail.
const NewsFeed = lazy(() => import('./pages/NewsFeed'));
const NewsArticle = lazy(() => import('./pages/NewsArticle'));

// Full waiver-wire trends list (dashboard card links here).
const WaiverTrends = lazy(() => import('./pages/WaiverTrends'));

// Sleeper tools: cross-league exposure report.
const Exposure = lazy(() => import('./pages/Exposure'));

// League-wide NFL context pages (Sleeper master derived).
const DepthCharts = lazy(() => import('./pages/DepthCharts'));
const Injuries = lazy(() => import('./pages/Injuries'));

// Native-app-only hub for tools without a tab-bar slot.
const More = lazy(() => import('./pages/More'));

// Native landing screen — brand hero + feature tour ("/" on native).
const NativeHome = lazy(() => import('./pages/NativeHome'));

// Settings — appearance + app data (native tab root).
const Settings = lazy(() => import('./pages/Settings'));

// One linked league: the user's team, roster, and draft handoff.
const LeagueTeam = lazy(() => import('./pages/LeagueTeam'));

// Help & guide — linked from More; public route.
const Help = lazy(() => import('./pages/Help'));

/**
 * Fires a GA4 page_view on every client-side route change (the SPA router
 * doesn't reload the page, so gtag's auto pageview never re-fires). Renders
 * nothing. Uses the full location key so query/hash changes count too.
 */
function AnalyticsListener() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Resets scroll to the top on every route change. Without this the SPA
 * carries the previous screen's scroll offset into the next one — switching
 * tabs mid-list landed you halfway down the new screen. Renders nothing.
 */
function ScrollReset() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

/**
 * Boundary around the routed screens. Keyed on the path so a screen that
 * threw clears itself when the user navigates away, instead of staying
 * broken until a full reload. The app-level boundary in main.tsx is the
 * backstop for anything that throws outside the routes (chrome, providers).
 */
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary variant="screen" resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-ink-muted">
      Loading…
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24">
      <h1 className="font-display text-3xl">404</h1>
      <p className="text-ink-muted">That page does not exist.</p>
      <Link to="/" className="text-accent underline">
        Back to home
      </Link>
    </div>
  );
}

/**
 * Holds the whole app behind a branded splash until the initial auth check
 * (`bootstrap()` → /v2/auth/me) settles. Without this, a returning user with a
 * stored token gets a flash of the signed-OUT UI (e.g. "Sign in" on the
 * landing) before /me resolves. Anonymous visitors (no stored token) resolve
 * `isBootstrapping` instantly, so they see no delay.
 */
function BootstrapGate({ children }: { children: ReactNode }) {
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const native = isNativeApp();
  // NATIVE: the animated boot screen, held a minimum 3s (owner call
  // 2026-07-31) with a 360ms fade-out once bootstrap settles.
  // WEB: no boot screen at all (audit: it flashed for every visitor
  // because isBootstrapping starts true) - just a neutral surface-colored
  // cover while /me resolves so returning users never see a flash of the
  // signed-out UI.
  //
  // The render below keeps `children` in the SAME fragment slot in every
  // state. The previous conditional-shape return made React tear down and
  // remount the entire app tree at the moment the boot screen unmounted
  // (audit finding), discarding the pre-warmed UI it existed to reveal.
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const [minHoldDone, setMinHoldDone] = useState(() => !native);
  useEffect(() => {
    if (minHoldDone) return undefined;
    const t = window.setTimeout(() => setMinHoldDone(true), 3000);
    return () => window.clearTimeout(t);
  }, [minHoldDone]);
  // Layout effect: on web the cover must clear BEFORE paint, or every
  // fast bootstrap (anonymous visitors) shows one opaque frame (audit F13).
  useLayoutEffect(() => {
    if (isBootstrapping || !minHoldDone || gone) return undefined;
    if (!native) {
      setGone(true); // web: drop the cover pre-paint, no animation
      return undefined;
    }
    setLeaving(true);
    const t = window.setTimeout(() => setGone(true), 360);
    return () => window.clearTimeout(t);
  }, [isBootstrapping, minHoldDone, gone, native]);

  return (
    <>
      {/* App mounts as soon as auth settles - under the overlay if one is
          still showing - and is never unmounted by the gate again. */}
      {!isBootstrapping ? children : null}
      {native && !gone ? <BootScreen leaving={leaving} /> : null}
      {!native && !gone ? (
        <div aria-hidden className="fixed inset-0 z-[60] bg-surface" />
      ) : null}
    </>
  );
}

export function App() {
  // Native shell (Capacitor iOS/Android) is a DIFFERENT product surface:
  // paid app, no marketing pages, no login wall, no external signup/billing
  // links (App Store 3.1.1 / 4.2). Web keeps the full marketing + SSO funnel.
  const native = isNativeApp();
  const [menuOpen, setMenuOpen] = useState(false);
  // Android's back button. No-op on web and iOS; without it the first back
  // press on Android exits the app from any screen.
  useAndroidBackButton();

  // Same rule the bar itself uses, so the reserve below can never disagree
  // with whether a bar is actually there.
  const location = useLocation();
  const tabBarHidden = isTabBarHidden(location.pathname);

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <AnalyticsListener />
      <ScrollReset />
      <BootstrapGate>
        {/* Persistent app chrome (native): wordmark + menu on EVERY
            screen; content slides underneath (owner brief). Subsumes the
            old status-bar scrim - the header's blur covers inset + bar. */}
        {native && (
          <>
            {/* Ambient bloom: the theme's own light pooling up from the
                bottom of the screen. Near-black themes read this as
                atmosphere rather than a gradient; quiet themes barely
                show it. Fixed + pointer-events-none, painted UNDER the
                content's stacking context. */}
            <div
              aria-hidden
              className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[55vh]"
              style={{
                background:
                  'radial-gradient(130% 100% at 50% 100%, var(--de-accent-glow), transparent 70%)',
              }}
            />
            {/* Full-page tint wash (light themes; transparent elsewhere). */}
            <div
              aria-hidden
              className="pointer-events-none fixed inset-0 z-0"
              style={{ background: 'var(--de-page-wash, transparent)' }}
            />
            <AppHeader onMenu={() => setMenuOpen(true)} />
            <HomeSidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
          </>
        )}
        <RouteSeo />
        {!native && <SiteHeader />}
        {/* Reserve space above the fixed tab bar: bar height + iPhone
            home-indicator safe-area.
            6rem was ~41px more than the ~55pt bar actually occupies, and it
            was applied even on /drafts/:id where the bar is HIDDEN, leaving
            roughly 130px of dead space at the bottom of the draft room. Now
            measured against the bar, and zero where there is no bar.
            The className and the inline style also both set padding; the
            inline one always won, so the class is gone. */}
        <div
          className={native ? 'relative z-10' : undefined}
          style={
            native
              ? {
                  paddingBottom: tabBarHidden
                    ? 'env(safe-area-inset-bottom)'
                    : 'calc(3.5rem + env(safe-area-inset-bottom))',
                }
              : undefined
          }
        >
        {/* Route-level boundary: a throw inside one screen must not take
            the shell and tab bar with it, and resetKey clears it on the
            next navigation so a bad screen is not sticky. */}
        <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* Native opens on its own landing screen (owner call: dropping
              straight into the Rankings list felt abrupt); web keeps the
              marketing homepage. */}
          <Route
            path="/"
            element={native ? <NativeHome /> : <Home />}
          />

          {/* Pricing / signup / billing live on draftedge.com — redirect out.
              Every in-app "Upgrade"/"Get started"/"Forgot password" link funnels
              through these. Pricing goes via the reverse-SSO bridge: a signed-in
              user lands on their DraftEdge subscription page already logged in;
              a logged-out visitor falls back to the public plans page.
              NATIVE: none of these exist — the paid app IS the license, and
              external purchase links are an App Store rejection (3.1.1). */}
          <Route
            path="/pricing"
            element={
              native ? (
                <Navigate to="/rankings" replace />
              ) : (
                <ExternalRedirect to={draftedgeSso('subscription', DRAFTEDGE.membership)} />
              )
            }
          />

          {/* Public news — list + article detail. */}
          <Route path="/news" element={<NewsFeed />} />
          <Route path="/news/:id" element={<NewsArticle />} />
          <Route path="/trending" element={<WaiverTrends />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/exposure" element={<Exposure />} />
          <Route path="/depth-charts" element={<DepthCharts />} />
          <Route path="/injuries" element={<Injuries />} />

          {/* Native tab-bar hub — secondary tools without a tab slot. */}
          <Route path="/more" element={<More />} />
          <Route path="/help" element={<Help />} />
          <Route path="/settings" element={<Settings />} />
          {/* Old Profile tab split into Home (connections/leagues) and
              Settings; redirect keeps stale links working. */}
          <Route path="/profile" element={<Navigate to="/" replace />} />
          <Route path="/league/:leagueId" element={<LeagueTeam />} />

          {/* Public rankings — shareable cheatsheet, no auth required. */}
          <Route path="/rankings" element={<Rankings />} />
          <Route path="/rookies" element={<Rookies />} />

          {/* Public player profile — deep dive linked from draft glance,
              cheatsheet, and search. No auth gate so URLs are shareable. */}
          <Route path="/players/:id" element={<PlayerDetail />} />

          {/* Public auth routes — bounce already-signed-in users away.
              NATIVE: there is no login (device-licensed paid app) — a
              reviewer landing on a sign-in form is a 2.1/5.1.1 rejection. */}
          <Route
            path="/login"
            element={
              native ? (
                <Navigate to="/rankings" replace />
              ) : (
                <RedirectIfAuthed>
                  <Login />
                </RedirectIfAuthed>
              )
            }
          />
          <Route
            path="/register"
            element={
              native ? (
                <Navigate to="/rankings" replace />
              ) : (
                <ExternalRedirect to={DRAFTEDGE.register} />
              )
            }
          />
          <Route
            path="/verify/:token"
            element={
              native ? <Navigate to="/rankings" replace /> : <VerifyEmail />
            }
          />
          <Route
            path="/password/forgot"
            element={
              native ? (
                <Navigate to="/rankings" replace />
              ) : (
                <ExternalRedirect to={DRAFTEDGE.forgotPassword} />
              )
            }
          />

          {/* Cross-domain SSO landing — draftedge.com hands off a signed token. */}
          <Route
            path="/sso"
            element={
              native ? <Navigate to="/rankings" replace /> : <SsoCallback />
            }
          />

          {/* Authed-only routes. NATIVE: Dashboard/Account carry external
              billing links ("Manage subscription on draftedge.com", "Buy
              more tokens") — hard 3.1.1 violations. Nothing in the native
              chrome links here; gate as belt-and-braces. */}
          <Route
            path="/dashboard"
            element={
              native ? (
                <Navigate to="/rankings" replace />
              ) : (
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              )
            }
          />
          <Route
            path="/account"
            element={
              native ? (
                <Navigate to="/rankings" replace />
              ) : (
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              )
            }
          />

          {/* Custom rankings editor — personal cheat sheet (Phase 4.1). */}
          <Route
            path="/cheatsheet"
            element={
              <ProtectedRoute>
                <Cheatsheet />
              </ProtectedRoute>
            }
          />

          {/* Drafts — the core of DraftEdge. */}
          <Route
            path="/drafts"
            element={
              <ProtectedRoute>
                <Drafts />
              </ProtectedRoute>
            }
          />
          {/* Mock Drafts tab — same screen as /drafts, opposite half of the
              list (owner brief: mocks are their own destination, real
              leagues stay on Drafts). */}
          <Route
            path="/mocks"
            element={
              <ProtectedRoute>
                <Drafts mode="mock" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drafts/new"
            element={
              <ProtectedRoute>
                <DraftNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drafts/:id"
            element={
              <ProtectedRoute>
                <DraftDetail />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </RouteErrorBoundary>
        </div>
        {native && <MobileTabBar />}
      </BootstrapGate>

      {/* Global toast outlet — sits on top of any route, safe-area aware. */}
      <ToastViewport />
    </div>
  );
}
